import classnames from 'classnames';
import { action, autorun, IObservableValue, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { assertNever } from '../util/types';

const styles = require('./loader.css');

// tslint:disable: interface-over-type-literal

export type LoadedValue<T> = {
  kind: 'value';
  value: T;
};

export type IsLoading = {
  kind: 'loading';
};

export type LoadingError = {
  kind: 'error';
  error: string;
};

export function loadedValue<T>(value: T): LoadedValue<T> {
  return {
    kind: 'value',
    value
  };
}

export function isLoading(): IsLoading {
  return {
    kind: 'loading'
  };
}

export function loadingError(error: string): LoadingError {
  return {
    kind: 'error',
    error
  };
}

/** Represents a value that may not (yet) have loaded */
export type MaybeLoading<T> = LoadedValue<T> | IsLoading | LoadingError;

/** Create a loading observable from a promise */
export function fromPromise<T>(futureValue: Promise<T>): MaybeLoading<T> {
  // Initially we are always in the loading state
  const loader: MaybeLoading<T> = observable<MaybeLoading<T>>({
    kind: 'loading'
  });

  // Resolve promise and update loader state in the background
  futureValue
    .then(value => {
      // TODO(ray): For some reason if the loaded value is an array then it does
      // not properly become an observable array after it gets inside the
      // observable. Added simple error for now.
      if (value instanceof Array) {
        throw new Error('arrays not supported for loaded value');
      }
      runInAction(() => {
        loader.kind = 'value';
        if (loader.kind === 'value') {
          loader.value = value;
        }
      });
    })
    .catch(error =>
      runInAction(() => {
        //tslint:disable-next-line:no-console
        console.error('encountered error while loading:', error);

        loader.kind = 'error';
        if (loader.kind === 'error') {
          loader.error = `${error}`;
        }

        // NOTE: Do not rethrow the error because it should be handled at the
        // service level, e.g. authorization errors. The service may choose to
        // throw an error to tell the loader component to show a message to the
        // user. Hence at this point it is considered "handled".
      })
    );

  // Return loading state, this will return as soon as the above promise chain is created
  // so should be '{kind: loading}' unless the promise has already been resolved
  return loader;
}

interface LoaderProps<T> {
  /** State of the data - loading or a terminal state */
  state: MaybeLoading<T>;
  /**
   * Component to render upon success.
   * IMPORTANT: Component must be an observer if it references observable data.
   * A function that returns a `JSX.Element` will typecheck correctly but it
   * will not react to changes in observables.
   */
  LoadedComponent: React.ComponentType<T>;
}

/**
 * Spinner over the viewport indicating something is loading.
 */
@observer
export class Spinner extends React.Component {
  /**
   * Whether the "render the overlay" lock has been taken across all spinners.
   * We only want to render the overlay once to avoid stacked blurring.
   */
  static overlayLockFree: IObservableValue<boolean> = observable.box(true);

  /** Whether this instance should render the overlay */
  @observable.ref hasOverlayLock: boolean = false;

  /** Disposer for the autorun to obtain the overlay rendering lock */
  disposeOverlayLockAutorun: () => void = () => {
    // init as noop
  };

  /** Take overlay rendering lock if it is available and watch for changes */
  @action componentDidMount() {
    this.disposeOverlayLockAutorun = autorun(() => {
      if (Spinner.overlayLockFree.get()) {
        runInAction(() => {
          this.hasOverlayLock = true;
          Spinner.overlayLockFree.set(false);
        });
      }
    });
  }

  /** Release overlay rendering lock if it is held */
  @action componentWillUnmount() {
    this.disposeOverlayLockAutorun();
    if (this.hasOverlayLock) {
      Spinner.overlayLockFree.set(true);
    }
  }

  /** Renders the component */
  render() {
    const className = classnames(
      styles.loader,
      /*
       * NOTE: The reason of always rendering the loader (as opposed to not
       * rendering if the lock is not held) is to cater for the common use case
       * of loading multiple pieces of data concurrently, which may complete at
       * different times. All of the loaders would have the same mount time and
       * hence the same rotational position of their spinners. So as their
       * siblings disappear the remaining spinners would continue to overlay
       * each other exactly.
       */
      this.hasOverlayLock ? styles.withOverlay : undefined
    );
    return (
      <div className={className}>
        <svg className={styles.circular} viewBox="25 25 50 50">
          <circle className={styles.path} cx="50" cy="50" r="20" fill="none" strokeWidth="2" strokeMiterlimit="10" />
        </svg>
      </div>
    );
  }
}

/**
 * Shows a loading placeholder until data has loaded.
 */
@observer
export class Loader<T> extends React.Component<LoaderProps<T>> {
  render() {
    const { state, LoadedComponent } = this.props;

    if (state.kind === 'loading') {
      return <Spinner />;
    } else if (state.kind === 'error') {
      return <div className={styles.errorText}>{state.error}</div>;
    } else if (state.kind === 'value') {
      return <LoadedComponent {...state.value} />;
    } else {
      return assertNever(state);
    }
  }
}



// WEBPACK FOOTER //
// ./src/loader/loader.tsx